import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Users, CalendarDays, Clock, History, PieChart, ChevronRight } from 'lucide-react';
import { User, OOORequest, Role } from '../types';
import { Badge, Modal } from './ui';
interface DashboardProps {
  currentUser: User;
  managedRole: Role | null;
  allUsers: User[];
  allRequests: OOORequest[];
}
function ProgressBar({
  used,
  total
}: {
  used: number;
  total: number;
}) {
  const percentage = Math.min(100, Math.max(0, used / total * 100)) || 0;
  const remaining = total - used;
  // Color logic: Red if <= 3 days left, Amber if <= 7 days left, Blue otherwise
  const barColor = remaining <= 3 ? 'bg-red-500' : remaining <= 7 ? 'bg-amber-500' : 'bg-[#0176d3]';
  return <div className="w-full" role="progressbar" aria-valuenow={used} aria-valuemin={0} aria-valuemax={total} data-id="element-94">
      <div className="flex justify-between text-xs mb-1" data-id="element-95">
        <span className="font-medium text-gray-700" data-id="element-96">{used} used</span>
        <span className="text-gray-500" data-id="element-97">{remaining} remaining</span>
      </div>
      <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-98">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${percentage}%`
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className={`h-full rounded-full ${barColor}`} data-id="element-99" />
      </div>
    </div>;
}
export function Dashboard({
  currentUser,
  managedRole,
  allUsers,
  allRequests
}: DashboardProps) {
  const isManager = managedRole !== null;
  // --- MANAGER DASHBOARD ---
  const team = useMemo(() => {
    if (!isManager) return [];
    return allUsers.filter(u => u.role === managedRole);
  }, [allUsers, isManager, managedRole]);
  const teamStats = useMemo(() => {
    const totalAllotted = team.reduce((sum, u) => sum + (u.ptoTotal || 0), 0);
    const totalUsed = team.reduce((sum, u) => sum + (u.ptoUsed || 0), 0);
    return {
      size: team.length,
      used: totalUsed,
      remaining: totalAllotted - totalUsed,
      total: totalAllotted
    };
  }, [team]);
  // --- INDIVIDUAL DASHBOARD ---
  const myRequests = useMemo(() => {
    if (isManager) return [];
    return allRequests.filter(r => r.userId === currentUser.id).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [allRequests, currentUser.id, isManager]);
  // Member whose request history is being viewed (manager drill-in)
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  const selectedMemberRequests = useMemo(() => {
    if (!selectedMember) return [];
    return allRequests.filter(r => r.userId === selectedMember.id).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [allRequests, selectedMember]);
  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };
  if (isManager) {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-6" data-id="element-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-101">
          Team PTO Dashboard
        </h2>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-102">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-103">
            <div className="p-3 bg-blue-50 text-[#0176d3] rounded-full" data-id="element-104">
              <Users className="w-6 h-6" data-id="element-105" />
            </div>
            <div data-id="element-106">
              <p className="text-sm font-medium text-gray-500" data-id="element-107">Team Members</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-108">
                {teamStats.size}
              </p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-109">
            <div className="p-3 bg-amber-50 text-amber-600 rounded-full" data-id="element-110">
              <CalendarDays className="w-6 h-6" data-id="element-111" />
            </div>
            <div data-id="element-112">
              <p className="text-sm font-medium text-gray-500" data-id="element-113">
                Total PTO Used
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-114">
                {teamStats.used}{' '}
                <span className="text-sm font-normal text-gray-500" data-id="element-115">days</span>
              </p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-116">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-full" data-id="element-117">
              <PieChart className="w-6 h-6" data-id="element-118" />
            </div>
            <div data-id="element-119">
              <p className="text-sm font-medium text-gray-500" data-id="element-120">
                Total PTO Remaining
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-121">
                {teamStats.remaining}{' '}
                <span className="text-sm font-normal text-gray-500" data-id="element-122">days</span>
              </p>
            </div>
          </div>
        </div>

        {/* Team List */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-123">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-124">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-125">
              Direct Reports Balances
            </h3>
          </div>
          {team.length === 0 ? <div className="p-12 text-center" data-id="element-126">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-127" />
              <p className="text-gray-500" data-id="element-128">No direct reports found.</p>
            </div> : <ul className="divide-y divide-gray-200" data-id="element-129">
              {team.map(user => <li key={user.id} data-id="element-130">
                  <button type="button" onClick={() => setSelectedMember(user)} aria-label={`View past requests for ${user.name}`} className="w-full text-left p-6 hover:bg-gray-50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-6 focus:outline-none focus:bg-gray-50 focus:ring-2 focus:ring-inset focus:ring-[#0176d3]" data-id="element-131">
                    <div className="md:w-1/3" data-id="element-132">
                      <h4 className="text-base font-semibold text-gray-900" data-id="element-133">
                        {user.name}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-134">{user.role}</p>
                    </div>
                    <div className="md:w-2/3 max-w-md w-full flex items-center gap-4" data-id="element-135">
                      <div className="flex-1" data-id="element-136">
                        <ProgressBar used={user.ptoUsed || 0} total={user.ptoTotal || 0} data-id="element-137" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-138" />
                    </div>
                  </button>
                </li>)}
            </ul>}
        </div>

        {/* Team Member Request History Modal */}
        <Modal isOpen={selectedMember !== null} onClose={() => setSelectedMember(null)} title={selectedMember ? `${selectedMember.name} — PTO Request History` : 'PTO Request History'} maxWidth="max-w-2xl" data-id="element-139">
          {selectedMember && <div className="space-y-4" data-id="element-140">
              {/* Balance summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 flex items-center justify-between text-sm" data-id="element-141">
                <span className="text-gray-500" data-id="element-142">PTO Balance</span>
                <span className="font-medium text-gray-900" data-id="element-143">
                  {selectedMember.ptoUsed || 0} used
                  <span className="text-gray-400 mx-2" data-id="element-144">·</span>
                  {(selectedMember.ptoTotal || 0) - (selectedMember.ptoUsed || 0)}{' '}
                  remaining
                  <span className="text-gray-400 mx-2" data-id="element-145">·</span>
                  {selectedMember.ptoTotal || 0} total
                </span>
              </div>

              {selectedMemberRequests.length === 0 ? <div className="p-10 text-center" data-id="element-146">
                  <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-147" />
                  <p className="text-gray-500" data-id="element-148">No past requests found.</p>
                </div> : <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden" data-id="element-149">
                  {selectedMemberRequests.map(request => <li key={request.id} className="p-4 space-y-3" data-id="element-150">
                      <Badge status={request.status} data-id="element-151" />
                      <div className="flex items-center text-sm text-gray-600" data-id="element-152">
                        <div className="flex items-center bg-gray-100 px-2.5 py-1 rounded" data-id="element-153">
                          <Clock className="w-4 h-4 mr-2 text-gray-500" data-id="element-154" />
                          <span className="font-medium" data-id="element-155">
                            {formatDate(request.startDate)}
                          </span>
                          <span className="mx-2 text-gray-400" data-id="element-156">→</span>
                          <span className="font-medium" data-id="element-157">
                            {formatDate(request.endDate)}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-158">
                        {request.message}
                      </div>
                      {request.status === 'Approved' && request.coverageIds && request.coverageIds.length > 0 && <div className="text-sm text-gray-600" data-id="element-159">
                            <span className="font-medium mr-2" data-id="element-160">
                              Coverage assigned:
                            </span>
                            {request.coverageIds.map(id => allUsers.find(u => u.id === id)?.name).join(', ')}
                          </div>}
                    </li>)}
                </ul>}
            </div>}
        </Modal>
      </motion.div>;
  }
  // --- INDIVIDUAL DASHBOARD ---
  const myTotal = currentUser.ptoTotal || 0;
  const myUsed = currentUser.ptoUsed || 0;
  const myRemaining = myTotal - myUsed;
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-8" data-id="element-161">
      <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-162">
        My PTO Dashboard
      </h2>

      {/* Personal Balance Summary */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-163">
        <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-8" data-id="element-164">
          <div className="flex-shrink-0 relative w-32 h-32 flex items-center justify-center" data-id="element-165">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36" data-id="element-166">
              <path className="text-gray-100" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" data-id="element-167" />
              <motion.path className={myRemaining <= 3 ? 'text-red-500' : myRemaining <= 7 ? 'text-amber-500' : 'text-[#0176d3]'} strokeWidth="3" strokeDasharray={`${myUsed / myTotal * 100}, 100`} stroke="currentColor" fill="none" initial={{
              strokeDasharray: '0, 100'
            }} animate={{
              strokeDasharray: `${myUsed / myTotal * 100}, 100`
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" data-id="element-168" />
            </svg>
            <div className="absolute flex flex-col items-center justify-center" data-id="element-169">
              <span className="text-2xl font-bold text-gray-900" data-id="element-170">
                {myRemaining}
              </span>
              <span className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-171">
                Left
              </span>
            </div>
          </div>

          <div className="flex-1 grid grid-cols-2 gap-6 w-full" data-id="element-172">
            <div data-id="element-173">
              <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-174">
                Total Allowance
              </p>
              <p className="text-3xl font-semibold text-gray-900" data-id="element-175">
                {myTotal}{' '}
                <span className="text-base font-normal text-gray-500" data-id="element-176">
                  days
                </span>
              </p>
            </div>
            <div data-id="element-177">
              <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-178">
                Days Used
              </p>
              <p className="text-3xl font-semibold text-gray-900" data-id="element-179">
                {myUsed}{' '}
                <span className="text-base font-normal text-gray-500" data-id="element-180">
                  days
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Request History */}
      <div data-id="element-181">
        <div className="flex items-center mb-4" data-id="element-182">
          <History className="w-5 h-5 text-gray-500 mr-2" data-id="element-183" />
          <h3 className="text-lg font-bold text-gray-900" data-id="element-184">
            PTO Request History
          </h3>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-185">
          {myRequests.length === 0 ? <div className="p-12 text-center" data-id="element-186">
              <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-187" />
              <p className="text-gray-500" data-id="element-188">You have no PTO request history.</p>
            </div> : <ul className="divide-y divide-gray-200" data-id="element-189">
              {myRequests.map(request => <li key={request.id} className="p-6 hover:bg-gray-50 transition-colors" data-id="element-190">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-191">
                    <div className="flex-1 space-y-3" data-id="element-192">
                      <div className="flex items-center space-x-3" data-id="element-193">
                        <Badge status={request.status} data-id="element-194" />
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-195">
                        <div className="flex items-center bg-gray-100 px-2.5 py-1 rounded" data-id="element-196">
                          <Clock className="w-4 h-4 mr-2 text-gray-500" data-id="element-197" />
                          <span className="font-medium" data-id="element-198">
                            {formatDate(request.startDate)}
                          </span>
                          <span className="mx-2 text-gray-400" data-id="element-199">→</span>
                          <span className="font-medium" data-id="element-200">
                            {formatDate(request.endDate)}
                          </span>
                        </div>
                      </div>

                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-201">
                        {request.message}
                      </div>

                      {request.status === 'Approved' && request.coverageIds && request.coverageIds.length > 0 && <div className="text-sm text-gray-600 flex items-center mt-2" data-id="element-202">
                            <span className="font-medium mr-2" data-id="element-203">
                              Coverage assigned:
                            </span>
                            {request.coverageIds.map(id => allUsers.find(u => u.id === id)?.name).join(', ')}
                          </div>}
                    </div>
                  </div>
                </li>)}
            </ul>}
        </div>
      </div>
    </motion.div>;
}